<script setup lang="ts">
import { computed, ref } from 'vue';
import { useThemeOverrides, useStringFunctions } from '@apparatix/composables';
import type { OpportunityType } from '@apparatix/types/Leads';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import DataTable, { DataTableRowClickEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import Badge from 'primevue/badge';
import ToggleSwitch from 'primevue/toggleswitch';
import type { Document as ApxDocument } from '@apparatix/types/Account';
import { Section, Typography } from '@apparatix/library/core';
import type { DetailTabName, ForwardNavigable } from './types';

type Props = {
  type: OpportunityType;
  documents: ApxDocument[];
  isLoading: boolean;
  tabId?: DetailTabName;
}

const props = defineProps<Props>();
const emit = defineEmits<ForwardNavigable>();

const { formatAsCurrency } = useStringFunctions();
const { datatable } = useThemeOverrides();

const showAllDocuments = ref(false);

const visibleDocuments = computed(() => showAllDocuments.value ? props.documents : props.documents?.filter(doc => doc.isActive));

const onDocRowClick = (e: DataTableRowClickEvent) => {
  emit('navigate-forward', { view: 'document', id: e.data.id, tabId: props.tabId });
};

const rowClass = () => 'cursor-pointer';

</script>
<template>
  <div class="w-full flex flex-col gap-6">
    <FormSkeleton v-if="props.isLoading" />
    <template v-else>
      <Section class="mb-3" :footer="false">
        <DataTable
          table-style="min-width: 50rem;"
          class="no-left-padding"
          :value="visibleDocuments"
          :dt="datatable.noLeftPadding"
          :row-class="rowClass"
          @row-click="onDocRowClick"
        >
          <template #header>
            <div class="flex items-center justify-between gap-4">
              <Typography variant="section-heading">
                Documents
                <Badge :value="visibleDocuments.length" severity="secondary" />
              </Typography>
              <div class="flex items-center gap-2">
                Show All
                <ToggleSwitch v-model="showAllDocuments" />
              </div>
            </div>
          </template>
          <template #empty>
            <p v-show="showAllDocuments">
              No documents exist for this {{ props.type }}.
            </p>
            <p v-show="!showAllDocuments">
              No active documents exist for this {{ props.type }}.
            </p>
          </template>
          <template v-if="visibleDocuments.length > 0">
            <Column field="name" header="Name" sortable>
              <template #body="slotProps">
                <a
                  v-show="slotProps.data.contractId === 0"
                  :href="`/apx/proposalTool/index-continue.html?advertiser=${slotProps.data.accountId}&tagid=&pid=${slotProps.data.id}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-[var(--text-link-primary)]"
                >
                  {{ slotProps.data.name }}
                </a>
                <a
                  v-show="slotProps.data.contractId !== 0"
                  :href="`/util/get/getContractPDFfromCFS.php?recordId=${slotProps.data.contractId}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-[var(--text-link-primary)]"
                >
                  {{ slotProps.data.name }}
                </a>
              </template>
            </Column>
            <Column field="status" header="Status" sortable />
            <Column field="type" header="Type" sortable />
            <Column field="age" header="Age (Days)" sortable />
            <Column field="totalFaces" header="Total Faces" sortable />
            <Column field="totalValue" header="Total Value" sortable>
              <template #body="{ data }">
                {{ formatAsCurrency(data.totalValue ?? 0) }}
              </template>
            </Column>
          </template>
        </DataTable>
      </Section>
    </template>
  </div>
</template>
